import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../../hooks/useApi';
import { copyText } from '../../../../utils/clipboard';
import { fmtDate } from '../../../../utils/ruDates';

const AGENT_DOWNLOAD_URL = '/downloads/releases/AnylexLocalAgent.exe';
const AGENT_RELEASE_URL = '/downloads/releases/agent-release.json';

const formatDate = value => {
  if (!value) return 'без срока';
  return fmtDate(value);
};

export default function LocalAgentSetup() {
  const { request, showStatus } = useApi();
  const [tokens, setTokens] = useState([]);
  const [devices, setDevices] = useState([]);
  const [release, setRelease] = useState({ version: '0.2.26' });
  const [secret, setSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const loadTokens = useCallback(async () => {
    setLoading(true);
    try {
      const [tokenRows, deviceRows] = await Promise.all([
        request('/api/v1/parser-agent/tokens'),
        request('/api/v1/parser-agent/devices'),
      ]);
      setTokens(tokenRows || []);
      setDevices(deviceRows || []);
    } catch (error) {
      showStatus('error', 'Не удалось загрузить ключи локального агента: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [request, showStatus]);

  useEffect(() => { loadTokens(); }, [loadTokens]);
  useEffect(() => {
    fetch(AGENT_RELEASE_URL, { cache: 'no-store' })
      .then(response => response.ok ? response.json() : Promise.reject(new Error('release unavailable')))
      .then(setRelease)
      .catch(() => {});
  }, []);

  const createToken = async () => {
    setCreating(true);
    try {
      const result = await request('/api/v1/parser-agent/tokens', {
        method: 'POST',
        body: JSON.stringify({
          name: `Windows-агент ${fmtDate()}`,
          expires_in_days: 365,
        }),
      });
      setSecret(result.token);
      await loadTokens();
      showStatus('success', 'Ключ создан. Скопируйте его сейчас — повторно он не показывается.');
    } catch (error) {
      showStatus('error', 'Не удалось создать ключ: ' + error.message);
    } finally {
      setCreating(false);
    }
  };

  const copySecret = async () => {
    const copied = await copyText(secret);
    showStatus(copied ? 'success' : 'error', copied ? 'Ключ скопирован' : 'Не удалось скопировать ключ');
  };

  const revokeToken = async id => {
    if (!window.confirm('Отозвать ключ? Агент с этим ключом перестанет брать задания — мониторинг и репрайсер встанут, пока не выпустите новый.')) return;
    setRevoking(id);
    try {
      await request(`/api/v1/parser-agent/tokens/${id}`, { method: 'DELETE' });
      await loadTokens();
      showStatus('success', 'Ключ отозван');
    } catch (error) {
      showStatus('error', 'Не удалось отозвать ключ: ' + error.message);
    } finally {
      setRevoking(null);
    }
  };

  const activeTokens = tokens.filter(token => !token.revoked_at);

  return (
    <div className="vm-agent-setup">
      <div className="vm-agent-setup__title">Подключение локального агента</div>
      <div className="vm-agent-setup__steps">
        <div><strong>1.</strong> Скачайте и запустите версию {release.version} для Windows.</div>
        <a className="btn btn--secondary btn--sm" href={`${AGENT_DOWNLOAD_URL}?v=${encodeURIComponent(release.version)}`} download>
          Скачать локальный агент {release.version}
        </a>
        <div><strong>2.</strong> Выпустите отдельный ключ и сохраните его в открывшемся приложении.</div>
        <button className="btn btn--primary btn--sm" onClick={createToken} disabled={creating}>
          {creating ? 'Создаём ключ...' : 'Создать новый ключ агента'}
        </button>
      </div>

      {secret && (
        <div className="vm-agent-setup__secret">
          <div className="vm-agent-setup__warning">Ключ показывается один раз. Скопируйте его до обновления страницы.</div>
          <input value={secret} readOnly onFocus={event => event.target.select()} />
          <button className="btn btn--primary btn--sm" onClick={copySecret}>Скопировать ключ</button>
        </div>
      )}

      <div className="vm-agent-setup__tokens">
        <span>{loading ? 'Проверяем ключи...' : `Активных ключей: ${activeTokens.length}`}</span>
        {activeTokens.map(token => (
          <span className="vm-agent-setup__token" key={token.id}>
            <code>{token.token_prefix}…</code> · до {formatDate(token.expires_at)}
            <button type="button" onClick={() => revokeToken(token.id)} disabled={revoking === token.id}>
              {revoking === token.id ? 'Отзываем...' : 'Отозвать'}
            </button>
          </span>
        ))}
      </div>
      <div className="vm-agent-setup__tokens">
        <strong>Устройства проекта: {devices.length}</strong>
        {devices.map(device => (
          <span className="vm-agent-setup__token" key={device.id}>
            {device.name} · {device.owner_name || 'пользователь не указан'} · {device.key_name}
            {' · '}{device.current_task_id
              ? 'выполняет задание'
              : device.status === 'cooldown'
                ? 'временно приостановлен'
                : device.status === 'offline' ? 'не в сети' : 'свободен'}
          </span>
        ))}
      </div>
      <div className="vm-cfg__hint">
        Затем нажмите в приложении «Войти в Ozon», авторизуйтесь, выберите регион и оставьте это окно Chrome открытым.
      </div>
    </div>
  );
}
